import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { employeesAPI } from "../api";
import { ChevronDown, ChevronUp, Star } from "lucide-react";
import { motion } from "framer-motion";

const scoreBadge = (score) => {
  if (score >= 4) return "badge-positive";
  if (score >= 3) return "badge-warning";
  return "badge-negative";
};

export default function EmployeesPage() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [sortField, setSortField] = useState("composite_score");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await employeesAPI.leaderboard({ days });
      setLeaderboard(res.data || []);
    } catch (err) {
      console.error("Employees fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sortedAgents = [...leaderboard].sort((a, b) => {
    const aVal = a[sortField] || 0;
    const bVal = b[sortField] || 0;
    return sortDir === "asc" ? aVal - bVal : bVal - aVal;
  });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-2xl font-bold text-gray-900">Nhân viên CSKH</h3>
          <p className="text-gray-500 text-sm mt-1">Bảng xếp hạng và hiệu suất nhân viên</p>
        </div>
        <div className="flex flex-wrap gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                days === d ? "bg-white text-primary-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {d === 365 ? "1 năm" : `${d} ngày`}
            </button>
          ))}
        </div>
      </div>

      {/* Top 3 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {sortedAgents.slice(0, 3).map((agent, i) => (
          <motion.div
            key={agent.id || i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="card cursor-pointer hover:shadow-lg transition-shadow relative overflow-hidden"
            onClick={() => navigate(`/employees/${agent.id}`)}
          >
            <div className={`absolute top-0 right-0 w-16 h-16 ${
              i === 0 ? "bg-yellow-400" : i === 1 ? "bg-gray-300" : "bg-amber-600"
            } opacity-10 rounded-bl-full`} />
            <div className="flex items-center space-x-3 mb-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                i === 0 ? "bg-yellow-500" : i === 1 ? "bg-gray-400" : "bg-amber-600"
              }`}>
                {i + 1}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{agent.name || `Agent ${agent.id}`}</p>
                <p className="text-xs text-gray-500">{agent.total_conversations || 0} hội thoại</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-green-50 rounded-lg p-2">
                <p className="text-sm font-bold text-green-600">{agent.avg_satisfaction || 0}/5</p>
                <p className="text-xs text-gray-500">Hài lòng</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-2">
                <p className="text-sm font-bold text-blue-600">{agent.composite_score || 0}/5</p>
                <p className="text-xs text-gray-500">Tổng hợp</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Full Table */}
      <div className="card overflow-x-auto">
        <table className="w-full min-w-[650px]">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">#</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Nhân viên</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort("total_conversations")}>
                Hội thoại <SortIcon field="total_conversations" />
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort("avg_satisfaction")}>
                Hài lòng <SortIcon field="avg_satisfaction" />
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort("avg_attitude")}>
                Thái độ <SortIcon field="avg_attitude" />
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort("avg_resolution")}>
                Chất lượng GQ <SortIcon field="avg_resolution" />
              </th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase cursor-pointer" onClick={() => handleSort("composite_score")}>
                Tổng hợp <SortIcon field="composite_score" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedAgents.map((agent, i) => (
              <tr
                key={agent.id || i}
                className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/employees/${agent.id}`)}
              >
                <td className="py-3 px-4 text-sm text-gray-500">{i + 1}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-700 font-semibold text-xs">{(agent.name || "A").charAt(0)}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-900">{agent.name || `Agent ${agent.id}`}</span>
                      <p className="text-xs text-gray-400">{agent.email || ""}</p>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-gray-700">{agent.total_conversations || 0}</td>
                <td className="py-3 px-4">
                  <span className={`badge ${scoreBadge(agent.avg_satisfaction)}`}>{agent.avg_satisfaction || 0}/5</span>
                </td>
                <td className="py-3 px-4">
                  <span className={`badge ${scoreBadge(agent.avg_attitude)}`}>{agent.avg_attitude || 0}/5</span>
                </td>
                <td className="py-3 px-4">
                  <span className={`badge ${scoreBadge(agent.avg_resolution)}`}>{agent.avg_resolution || 0}/5</span>
                </td>
                <td className="py-3 px-4">
                  <span className={`badge ${scoreBadge(agent.composite_score)} font-bold`}>{agent.composite_score || 0}/5</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
